import * as React from "react";
import { Alert, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

import { AppErrorBoundary } from "@/layouts/AppErrorBoundary";
import { AppShell } from "@/layouts/AppShell";
import { LandingPage } from "@/features/landing/LandingPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { SessionsIndex } from "@/features/sessions/SessionsIndex";
import { RequireAuth } from "@/features/auth/RequireAuth";


/** Lazy-loaded routes. Each ``React.lazy`` boundary becomes its own
 *  Vite chunk, so the initial bundle stays focused on what's needed
 *  for the first paint (auth + ``AppShell`` + the sessions index —
 *  the route every user lands on after login).
 *
 *  Pre-split the bundle was a single ~2 MB / 660 KB gzipped JS file.
 *  After this change, the initial paint downloads only the shell +
 *  sessions-index chunk; the rest streams in as the user navigates.
 *
 *  Heavy chunks split out below — labelled by user-visible surface:
 *
 *  * ``SessionView`` — chat UI + message list + workspace tree +
 *    model-settings popover + MR-link discovery + export-document
 *    dialog. Users open this within a few seconds of landing on the
 *    index, so under realistic conditions the chunk is fetched while
 *    they're scanning the list.
 *  * ``DocumentLibraryPage`` — document cards, drift chips, PDF
 *    preview, iterate-from picker. Many users never touch this.
 *  * Admin pages — operator-only. Each tab is its own ``lazy()`` so
 *    Vite groups co-located dynamic imports into a single admin
 *    chunk; navigating between admin tabs hits an already-loaded
 *    chunk after the first.
 *  * Less-trafficked surfaces (``SourceViewer``, ``ChangelogPage``,
 *    ``ArchivedSessions``, ``SessionsBrowsePage``, ``AccountPage``)
 *    — each one is small individually but every page that lazy-
 *    loads is one fewer module in the initial bundle. */
function lazyExport<
  K extends string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic lazy-loader accepts any component props
  T extends Record<K, React.ComponentType<any>>,
>(loader: () => Promise<T>, key: K): T[K] {
  // ``React.lazy`` only knows about the ``default`` export shape;
  // wrap loader() to project the named export into ``default``.
  // The cast keeps the source component's props (e.g.
  // ``SessionView``'s ``sessionId``) typed at the call site.
  return React.lazy(() =>
    loader().then((m) => ({ default: m[key] })),
  ) as unknown as T[K];
}

const SessionView = lazyExport(
  () => import("@/features/sessions/SessionView"),
  "SessionView",
);
const DemoSessionView = lazyExport(
  () => import("@/features/demo/DemoSessionView"),
  "DemoSessionView",
);
const DocumentLibraryPage = lazyExport(
  () => import("@/features/documents/DocumentLibraryPage"),
  "DocumentLibraryPage",
);
const SourceViewer = lazyExport(
  () => import("@/features/sessions/SourceViewer"),
  "SourceViewer",
);
const ArchivedSessions = lazyExport(
  () => import("@/features/sessions/ArchivedSessions"),
  "ArchivedSessions",
);
const SessionsBrowsePage = lazyExport(
  () => import("@/features/sessions/SessionsBrowsePage"),
  "SessionsBrowsePage",
);
const AccountPage = lazyExport(
  () => import("@/features/account-link/AccountPage"),
  "AccountPage",
);
const ChangelogPage = lazyExport(
  () => import("@/features/changelog/ChangelogPage"),
  "ChangelogPage",
);
const AdminLayout = lazyExport(
  () => import("@/features/admin/AdminLayout"),
  "AdminLayout",
);
const WelcomePage = lazyExport(
  () => import("@/features/onboarding/WelcomePage"),
  "WelcomePage",
);
const LegalPage = lazyExport(
  () => import("@/features/legal/LegalPage"),
  "LegalPage",
);
const LegalVersionsPage = lazyExport(
  () => import("@/features/legal/LegalPage"),
  "LegalVersionsPage",
);
const LegalChangesPage = lazyExport(
  () => import("@/features/legal/LegalPage"),
  "LegalChangesPage",
);
const LegalFieldsPage = lazyExport(
  () => import("@/features/legal/LegalPage"),
  "LegalFieldsPage",
);
const WelcomeTermsPage = lazyExport(
  () => import("@/features/legal/WelcomeTermsPage"),
  "WelcomeTermsPage",
);
const InvitationAcceptPage = lazyExport(
  () => import("@/features/auth/InvitationAcceptPage"),
  "InvitationAcceptPage",
);
const CodeSourceSetupPage = lazyExport(
  () => import("@/features/setup/CodeSourceSetupPage"),
  "CodeSourceSetupPage",
);

// Admin tabs — every dynamic import below resolves into the same
// ``apps/frontend/src/features/admin/tabs.tsx`` module, so Vite
// groups them into a single chunk. Navigating between tabs after
// the first one is then a no-op fetch.
const adminTabsLoader = () => import("@/features/admin/tabs");
const OverviewTab = lazyExport(adminTabsLoader, "OverviewTab");
const UsersTab = lazyExport(adminTabsLoader, "UsersTab");
const OrgsTab = lazyExport(adminTabsLoader, "OrgsTab");
const ConfigTab = lazyExport(adminTabsLoader, "ConfigTab");
const BrandingTab = lazyExport(adminTabsLoader, "BrandingTab");
const SsoTab = lazyExport(adminTabsLoader, "SsoTab");
const MembersTab = lazyExport(adminTabsLoader, "MembersTab");
const PromptsTab = lazyExport(adminTabsLoader, "PromptsTab");
const ConnectionsTab = lazyExport(adminTabsLoader, "ConnectionsTab");
const CodeSourcesTab = lazyExport(adminTabsLoader, "CodeSourcesTab");
const LlmProvidersTab = lazyExport(adminTabsLoader, "LlmProvidersTab");
const AddonsTab = lazyExport(adminTabsLoader, "AddonsTab");
const CostsTab = lazyExport(adminTabsLoader, "CostsTab");
const CreditsTab = lazyExport(adminTabsLoader, "CreditsTab");
const BillingTab = lazyExport(adminTabsLoader, "BillingTab");
const BillingReportsTab = lazyExport(adminTabsLoader, "BillingReportsTab");
const OperationsTab = lazyExport(adminTabsLoader, "OperationsTab");
const ObservabilityTab = lazyExport(adminTabsLoader, "ObservabilityTab");
const FeedbackTab = lazyExport(adminTabsLoader, "FeedbackTab");
const MaintenanceTab = lazyExport(adminTabsLoader, "MaintenanceTab");
const SystemTab = lazyExport(adminTabsLoader, "SystemTab");


/** Wrap a lazy element in a Suspense boundary with a thin loading
 *  bar — keeps the AppShell chrome painted while the route's chunk
 *  downloads. The bar is preferable to a spinner because the
 *  network wait is short and a flash of "loading" looks broken. */
function Lazy({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <React.Suspense fallback={<LinearProgress sx={{ borderRadius: 0 }} />}>
      {children}
    </React.Suspense>
  );
}


export const router = createBrowserRouter([
  { path: "/", element: <AppErrorBoundary><LandingPage /></AppErrorBoundary> },
  { path: "/login", element: <AppErrorBoundary><LoginPage /></AppErrorBoundary> },
  {
    // Terms, Privacy Policy, DPA and sub-processors. Public and outside
    // the AppShell: they must be readable before an account exists, and
    // the login page links them. An unknown :doc renders the 404 page.
    path: "/legal/:doc",
    element: (
      <AppErrorBoundary>
        <Lazy><LegalPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // Every published version of a document, public like the documents
    // (the sub-processor list links its history here).
    path: "/legal/:doc/versions",
    element: (
      <AppErrorBoundary>
        <Lazy><LegalVersionsPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // What changed between two versions (item 113), public like the
    // documents: the org prompt, the update notice, the version history
    // and the notice emails link here.
    path: "/legal/:doc/changes",
    element: (
      <AppErrorBoundary>
        <Lazy><LegalChangesPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // The organisation export's field reference, linked from the
    // portability information (section 2.1). Public like the documents.
    path: "/legal/portability/fields",
    element: (
      <AppErrorBoundary>
        <Lazy><LegalFieldsPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // A first sign-in accepts the Terms here before the account exists
    // (the sign-in callback sends a new identity to this page). Public
    // and outside RequireAuth: there is no session yet.
    path: "/welcome/terms",
    element: (
      <AppErrorBoundary>
        <Lazy><WelcomeTermsPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // Public org-invitation landing. The backend emails
    // ``/invitations/accept?token=…``; this route reads the token,
    // ensures the recipient is signed in (bouncing through /login when
    // not), then POSTs to the accept endpoint. Standalone — a brand-new
    // invitee may have no org yet, for whom the AppShell chrome is empty.
    path: "/invitations/accept",
    element: (
      <AppErrorBoundary>
        <Lazy><InvitationAcceptPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // A code-source setup link (FW-32): public, no sign-in. The token rides
    // in the fragment (``#t=…``), so it never reaches a server log; the page
    // swaps it for a scoped session and drops it from the address.
    path: "/setup/code-source",
    element: (
      <AppErrorBoundary>
        <Lazy><CodeSourceSetupPage /></Lazy>
      </AppErrorBoundary>
    ),
  },
  {
    // Standalone (outside AppShell): the shell's org-scoped chrome
    // is empty/broken for a user with no org, which is exactly who
    // this page exists for. RequireAuth routes org-less users here.
    path: "/app/welcome",
    element: (
      <AppErrorBoundary>
        <RequireAuth>
          <Lazy><WelcomePage /></Lazy>
        </RequireAuth>
      </AppErrorBoundary>
    ),
  },
  {
    path: "/app",
    element: (
      <RequireAuth>
        <AppErrorBoundary>
          <AppShell />
        </AppErrorBoundary>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="sessions" replace /> },
      { path: "sessions", element: <SessionsIndex /> },
      {
        path: "sessions/archived",
        element: <Lazy><ArchivedSessions /></Lazy>,
      },
      // The read-only demo (FW-34): a static path, so it outranks the
      // ``:sessionId`` route and never reaches SessionView.
      { path: "sessions/demo", element: <Lazy><DemoSessionView /></Lazy> },
      { path: "sessions/:sessionId", element: <KeyedSessionView /> },
      { path: "browse", element: <Lazy><SessionsBrowsePage /></Lazy> },
      { path: "source", element: <Lazy><SourceViewer /></Lazy> },
      { path: "account", element: <Lazy><AccountPage /></Lazy> },
      { path: "changelog", element: <Lazy><ChangelogPage /></Lazy> },
      { path: "documents", element: <Lazy><DocumentLibraryPage /></Lazy> },
      {
        path: "admin",
        element: <Lazy><AdminLayout /></Lazy>,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          { path: "overview", element: <Lazy><OverviewTab /></Lazy> },
          { path: "members", element: <Lazy><MembersTab /></Lazy> },
          { path: "users", element: <Lazy><UsersTab /></Lazy> },
          { path: "orgs", element: <Lazy><OrgsTab /></Lazy> },
          { path: "config", element: <Lazy><ConfigTab /></Lazy> },
          { path: "branding", element: <Lazy><BrandingTab /></Lazy> },
          { path: "sso", element: <Lazy><SsoTab /></Lazy> },
          { path: "prompts", element: <Lazy><PromptsTab /></Lazy> },
          { path: "connections", element: <Lazy><ConnectionsTab /></Lazy> },
          { path: "code-sources", element: <Lazy><CodeSourcesTab /></Lazy> },
          { path: "llm-providers", element: <Lazy><LlmProvidersTab /></Lazy> },
          { path: "addons", element: <Lazy><AddonsTab /></Lazy> },
          { path: "costs", element: <Lazy><CostsTab /></Lazy> },
          { path: "credits", element: <Lazy><CreditsTab /></Lazy> },
          { path: "billing", element: <Lazy><BillingTab /></Lazy> },
          { path: "billing-reports", element: <Lazy><BillingReportsTab /></Lazy> },
          { path: "obs", element: <Lazy><ObservabilityTab /></Lazy> },
          { path: "feedback", element: <Lazy><FeedbackTab /></Lazy> },
          { path: "operations", element: <Lazy><OperationsTab /></Lazy> },
          { path: "operations/:section", element: <Lazy><OperationsTab /></Lazy> },
          // The four infra tabs became sections of Operations; keep
          // bookmarks and in-app links working.
          { path: "fs", element: <Navigate to="/app/admin/operations/fs" replace /> },
          { path: "sessions", element: <Navigate to="/app/admin/operations/sessions" replace /> },
          { path: "worktrees", element: <Navigate to="/app/admin/operations/worktrees" replace /> },
          { path: "codex", element: <Navigate to="/app/admin/operations/codex" replace /> },
          { path: "maintenance", element: <Lazy><MaintenanceTab /></Lazy> },
          { path: "system", element: <Lazy><SystemTab /></Lazy> },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);


/** Force a remount of ``SessionView`` whenever the ``:sessionId``
 *  route param changes.
 *
 *  React Router re-renders the element in-place when only a path
 *  param changes — hooks, state, and refs persist. For SessionView
 *  that used to leak state between sessions: the ``streamUrl`` /
 *  ``activeTurnId`` local state, the ``useTurnStream`` subscription,
 *  the ``hasHydrated`` ref, and the ``draft`` composer buffer all
 *  kept pointing at the previously-viewed session. The user would
 *  start a turn on session A, navigate to session B, and still see
 *  A's "the agent is thinking" indicator plus A's streamed messages
 *  in B's pane.
 *
 *  Passing ``sessionId`` as the ``key`` forces React to unmount /
 *  remount the subtree on every navigation, wiping all local state
 *  atomically. Cheaper and less error-prone than hand-resetting
 *  every piece of state with a ``useEffect(..., [sessionId])``.
 *  Splitting this one wrapper out into its own file would obscure
 *  why it exists — the router definition is the natural home. */
// eslint-disable-next-line react-refresh/only-export-components
function KeyedSessionView(): React.JSX.Element {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  // The route is ``/sessions/:sessionId`` so the param is always
  // present when this component mounts. The ?? guard is purely
  // defensive against a future router change; we don't render the
  // SessionView body without a real id because every hook inside
  // it depends on one.
  if (!sessionId) return <Alert severity="error">{t("session.missing_id")}</Alert>;
  return (
    <Lazy>
      <SessionView key={sessionId} sessionId={sessionId} />
    </Lazy>
  );
}
